/*
 * What Email Bison accepts as the files of one reply, and how a file a rep picks is named and typed
 * on the way (docs/2026-10-19-inbox-reply-attachments-implementation-plan.md, section 3). Pure and
 * browser-safe: the reply box and the server refuse the same things in the same words.
 *
 * Bison documents "individual max size: 10MB" and "Combined max size: 25MB" for the reply and forward
 * endpoints (public OpenAPI, re-read 2026-10-20) and no file count. Its "MB" is 2^20 bytes of the raw
 * file: on the configured instance, 2026-10-21 (delivery-QA probes P1 to P3, owner-approved, ledger
 * `61c7402f`), it took one file of 10,000,000 raw bytes (13.3 MB as sent), one of 10,485,760, and
 * three making 25,500,000 together. Higher sizes were not tried, so the limits stop at 10 and 25 MiB.
 * Which types it takes is its own list, checked on content (`reply-attachment-types.ts`).
 */

/** Bytes one file may have, and all of one reply's files together: 10 MiB and 25 MiB. */
export const BISON_FILE_LIMITS = { fileBytes: 10_485_760, combinedBytes: 26_214_400 } as const;

/** True since the probes above settled Bison's units; false keeps files off every reply. */
export const BISON_FILE_LIMITS_SETTLED = true;

/** The type a file is carried as when the browser named none, or none that parses. */
export const OPAQUE_MEDIA_TYPE = "application/octet-stream";

export type ReplyFileRefusal =
  | "file_empty"
  | "file_too_large"
  | "total_too_large"
  | "file_name_invalid"
  | "file_type_unsupported";

/** A per-file and a per-reply ceiling, in bytes. */
export type FileLimits = { readonly fileBytes: number; readonly combinedBytes: number };

/**
 * Why these sizes cannot go on one reply, or null. `limits` is Bison's everywhere the app sends; only
 * the delivery-QA provider probe passes its own, to learn where Bison's real boundary lies.
 */
export function replyFileSizeRefusal(
  sizes: readonly number[],
  limits: FileLimits = BISON_FILE_LIMITS,
): ReplyFileRefusal | null {
  if (sizes.some((size) => size < 1)) return "file_empty";
  if (sizes.some((size) => size > limits.fileBytes)) return "file_too_large";
  const total = sizes.reduce((sum, size) => sum + size, 0);
  return total > limits.combinedBytes ? "total_too_large" : null;
}

const MAX_NAME_LENGTH = 255;
/** A control character or a path separator: a name is a label in the email, never a path. */
const unsafeCharacter = (character: string): boolean => {
  const code = character.codePointAt(0) ?? 0;
  return code < 0x20 || code === 0x7f || character === "/" || character === "\\";
};

/** The file's name as it will be shown and sent, or null when it cannot be one. */
export function replyFileName(raw: string): string | null {
  const name = raw.normalize("NFC").trim();
  if (name === "" || name === "." || name === ".." || [...name].some(unsafeCharacter)) return null;
  // Counted as the send's form counts it (`bison-actions.ts`), so a name kept here is one it sends.
  return name.length <= MAX_NAME_LENGTH ? name : null;
}

// RFC 6838 type and subtype names; parameters such as `charset` are not carried.
const MEDIA_TYPE = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,63}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,63}$/u;

/** The media type a file is carried as: the browser's, when it is a well-formed one, else opaque. */
export function replyFileMediaType(declared: string): string {
  const type = declared.split(";")[0]?.trim().toLowerCase() ?? "";
  return MEDIA_TYPE.test(type) ? type : OPAQUE_MEDIA_TYPE;
}

/** "2.3 MB", "830 KB": a size in the units Bison counts its limits in, 2^20 bytes to its MB. */
export function replyFileSizeLabel(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1_024))} KB`;
}

type ReplyFilesUnavailable =
  | "sending_off"
  | "switched_off"
  | "update_required"
  | "limits_unsettled"
  | "storage_unavailable";
export type ReplyFilesAvailability =
  | { readonly available: true }
  | { readonly available: false; readonly reason: ReplyFilesUnavailable };

/** The first thing, in this order, that keeps files off a reply here. Pure. */
export function replyFilesAvailability(input: {
  dispatchable: boolean;
  limitsSettled: boolean;
}): ReplyFilesAvailability {
  if (!input.dispatchable) return { available: false, reason: "update_required" };
  if (!input.limitsSettled) return { available: false, reason: "limits_unsettled" };
  return { available: true };
}
